/**
 * How long a portal session may last (TASK-649, INC-710).
 *
 * A signed-in portal session is admin over the whole fleet. It ends 12 hours
 * after sign-in, whatever the person does, and after 2 hours with no input in
 * any open portal tab. Cognito ends the refresh token at 12 hours; it has no
 * idle timeout, so only the page enforces the 2 hours.
 *
 * Pure functions on wall-clock times. A background tab that the browser
 * throttles, or a laptop that wakes from sleep, gets the right answer the
 * moment it runs again, because nothing here counts timer ticks.
 */

export interface SessionLimits {
  /** No input for this long ends the session. */
  idleMs: number;
  /** The warning shows this long before the idle end. */
  warningMs: number;
  /** The session ends this long after sign-in. */
  maxAgeMs: number;
}

export const DEFAULT_LIMITS: SessionLimits = {
  idleMs: 2 * 60 * 60 * 1000,
  warningMs: 2 * 60 * 1000,
  maxAgeMs: 12 * 60 * 60 * 1000,
};

/** Why a session ended. 'manual' is the Sign out button. */
export type EndReason = 'idle' | 'max-age' | 'expired' | 'manual';

export type SessionState =
  | { kind: 'active' }
  | { kind: 'warning'; endsAt: number }
  | { kind: 'ended'; reason: 'idle' | 'max-age' };

export function sessionState(
  now: number,
  signedInAt: number,
  lastInputAt: number | null,
  limits: SessionLimits
): SessionState {
  if (!Number.isFinite(signedInAt) || now - signedInAt >= limits.maxAgeMs) {
    return { kind: 'ended', reason: 'max-age' };
  }
  // No record of input means no proof of a person: end it.
  if (lastInputAt === null || !Number.isFinite(lastInputAt)) {
    return { kind: 'ended', reason: 'idle' };
  }
  const idleEndsAt = lastInputAt + limits.idleMs;
  if (now >= idleEndsAt) return { kind: 'ended', reason: 'idle' };
  if (now >= idleEndsAt - limits.warningMs) return { kind: 'warning', endsAt: idleEndsAt };
  return { kind: 'active' };
}

/** When the state can next change: the moment the warning shows, or the end. */
export function nextChangeAt(state: SessionState, lastInputAt: number, limits: SessionLimits): number {
  if (state.kind === 'warning') return state.endsAt;
  return lastInputAt + limits.idleMs - limits.warningMs;
}

const TEST_LIMITS_KEY = 'portal_session_test_limits';

/**
 * The limits in force. On localhost (Vite dev server only) a tester may
 * shorten them with localStorage `portal_session_test_limits`, for example
 * {"idleMs":180000,"warningMs":60000}. Shorten only: a longer value is
 * ignored. A production build removes this branch.
 */
export function currentLimits(): SessionLimits {
  if (import.meta.env?.DEV) {
    try {
      const raw = localStorage.getItem(TEST_LIMITS_KEY);
      if (raw) {
        const wanted = JSON.parse(raw) as Partial<SessionLimits>;
        const shorter = (key: keyof SessionLimits) => {
          const value = Number(wanted[key]);
          return Number.isFinite(value) && value > 0 && value < DEFAULT_LIMITS[key] ? value : DEFAULT_LIMITS[key];
        };
        return { idleMs: shorter('idleMs'), warningMs: shorter('warningMs'), maxAgeMs: shorter('maxAgeMs') };
      }
    } catch {
      // A bad test value: use the real limits.
    }
  }
  return DEFAULT_LIMITS;
}
